import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CalendarEntry, CalendarEntryStatus } from '@contracts/interfaces/calendar.interface';
import { CalendarEntryDialogComponent } from '../../dialogs/calendar-entry-dialog/calendar-entry-dialog.component';
import { CalendarPickerDialogComponent } from '../../dialogs/calendar-picker-dialog/calendar-picker-dialog.component';
import { CalendarService } from '../../services/calendar.service';

@Component({
  selector: 'app-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss'],
})
export class CalendarComponent implements OnInit {
  CalendarEntryStatus = CalendarEntryStatus;
  selectedDay?: Date;

  entries: CalendarEntry[] = [];
  daySlots: { date: Date; label: string; entries: CalendarEntry[]; isToday: boolean; dayClass: DayClass }[] = [];
  beyondEntries: CalendarEntry[] = [];
  pastEntries: CalendarEntry[] = [];
  isLoading = false;

  constructor(
    private dialog: MatDialog,
    private calendarService: CalendarService
  ) {}

  ngOnInit(): void {
    this.loadEntries();
  }

  get upcomingEntries(): CalendarEntry[] {
    const todayKey = this.dayKey(this.startOfToday());
    return this.entries
      .filter((entry) => entry.status === CalendarEntryStatus.Upcoming && this.dayKey(this.asDate(entry)) >= todayKey)
      .sort((a, b) => {
        const pinnedCompare = (a.pinned ? -1 : 0) - (b.pinned ? -1 : 0);
        if (pinnedCompare !== 0) {
          return pinnedCompare;
        }
        return this.asDate(a).getTime() - this.asDate(b).getTime();
      });
  }

  loadEntries(): void {
    this.isLoading = true;
    this.calendarService.find().subscribe({
      next: (entries) => {
        this.entries = (entries || []).map((entry) => ({
          ...entry,
          id: entry.id || (entry as any)._id,
          scheduledAt: entry.scheduledAt ? new Date(entry.scheduledAt) : entry.scheduledAt,
        }));
        this.buildDaySlots();
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      },
    });
  }

  openCreate(): void {
    const blankEntry: CalendarEntry = {
      id: `entry-${Date.now()}`,
      title: '',
      scheduledAt: new Date(),
      description: '',
      category: '',
      status: CalendarEntryStatus.Upcoming,
      pinned: false,
    };

    const dialogRef = this.dialog.open(CalendarEntryDialogComponent, {
      width: '540px',
      data: { entry: blankEntry, isNew: true },
    });

    dialogRef.afterClosed().subscribe((created: CalendarEntry | undefined) => {
      if (!created) {
        return;
      }

      this.calendarService.create(created).subscribe((resp) => {
        this.entries = [...this.entries, { ...created, id: resp.id }];
        this.buildDaySlots();
      });
    });
  }

  openDayPicker(): void {
    const dialogRef = this.dialog.open(CalendarPickerDialogComponent, {
      width: '840px',
      data: { selected: this.selectedDay || new Date() },
    });

    dialogRef.afterClosed().subscribe((picked: Date | undefined) => {
      if (!picked) {
        return;
      }

      this.selectedDay = picked;
      this.openCreateForDay(picked);
    });
  }

  private openCreateForDay(date: Date): void {
    const blankEntry: CalendarEntry = {
      id: `entry-${Date.now()}`,
      title: '',
      scheduledAt: date,
      description: '',
      category: '',
      status: CalendarEntryStatus.Upcoming,
      pinned: false,
    };

    const dialogRef = this.dialog.open(CalendarEntryDialogComponent, {
      width: '540px',
      data: { entry: blankEntry, isNew: true },
    });

    dialogRef.afterClosed().subscribe((created: CalendarEntry | undefined) => {
      if (!created) {
        return;
      }

      this.calendarService.create(created).subscribe((resp) => {
        this.entries = [...this.entries, { ...created, id: resp.id }];
        this.buildDaySlots();
      });
    });
  }

  asDate(entry: CalendarEntry): Date {
    return entry.scheduledAt instanceof Date ? entry.scheduledAt : new Date(entry.scheduledAt);
  }

  timeUntil(entry: CalendarEntry): string {
    const diffMs = this.asDate(entry).getTime() - Date.now();

    if (diffMs <= 0) {
      return 'Due now';
    }

    const minutes = Math.floor(diffMs / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) {
      return `${days}d ${hours % 24}h`;
    }

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    }

    return `${minutes}m`;
  }

  openEdit(entry: CalendarEntry): void {
    const dialogRef = this.dialog.open(CalendarEntryDialogComponent, {
      width: '540px',
      data: { entry, isNew: false },
    });

    dialogRef.afterClosed().subscribe((result: CalendarEntry | { delete: true; entry: CalendarEntry } | undefined) => {
      if (!result) {
        return;
      }

      if ((result as any).delete) {
        const toDelete = (result as any).entry as CalendarEntry;
        this.calendarService.delete(toDelete.id).subscribe(() => {
          this.loadEntries();
        });
        return;
      }

      const updated = result as CalendarEntry;
      this.calendarService.update(updated).subscribe(() => {
        this.loadEntries();
      });
    });
  }

  openReschedule(entry: CalendarEntry): void {
    const dialogRef = this.dialog.open(CalendarPickerDialogComponent, {
      width: '840px',
      data: { selected: this.asDate(entry) },
    });

    dialogRef.afterClosed().subscribe((picked: Date | undefined) => {
      if (!picked) {
        return;
      }

      const updated: CalendarEntry = { ...entry, scheduledAt: picked };

      this.calendarService.update(updated).subscribe(() => {
        this.loadEntries();
      });
    });
  }

  markComplete(entry: CalendarEntry): void {
    this.updateEntryStatus(entry, CalendarEntryStatus.Completed);
  }

  toggleCompletion(entry: CalendarEntry): void {
    const nextStatus =
      entry.status === CalendarEntryStatus.Completed
        ? CalendarEntryStatus.Upcoming
        : CalendarEntryStatus.Completed;

    this.updateEntryStatus(entry, nextStatus);
  }

  entryOpacity(entry: CalendarEntry): number {
    const now = Date.now();

    const date = entry.scheduledAt ? this.asDate(entry) : null;
    if (!date) {
      return 1;
    }

    const entryDate = date.getTime();
    if (isNaN(entryDate)) {
      return 1;
    }

    const fourteenDaysMs = 14 * 24 * 60 * 60 * 1000;
    const sixtyDaysMs = 60 * 24 * 60 * 60 * 1000;
    
    const ageMs = Math.max(0, now - entryDate);
    if (ageMs <= fourteenDaysMs) {
      return 1;
    }
    if (ageMs >= sixtyDaysMs) {
      return 0.5;
    }

    const fadeRange = sixtyDaysMs - fourteenDaysMs;
    const fadeProgress = Math.min(
      1,
      Math.max(0, (ageMs - fourteenDaysMs) / fadeRange)
    );

    const opacity = 1 - fadeProgress * 0.5
    return opacity;
  }

  private updateEntryStatus(entry: CalendarEntry, status: CalendarEntryStatus): void {
    if (entry.status === status) {
      return;
    }

    const updated: CalendarEntry = { ...entry, status };

    this.calendarService.update(updated).subscribe(() => {
      this.entries = this.entries.map((current) => (current.id === updated.id ? updated : current));
      this.buildDaySlots();
    });
  }

  private buildDaySlots(): void {
    const activeEntries = this.entries.filter((entry) => entry.status !== CalendarEntryStatus.Completed);
    const start = this.startOfToday();
    const end = new Date(start);
    end.setDate(start.getDate() + 13);
    end.setHours(23, 59, 59, 999);

    const slots: { date: Date; label: string; entries: CalendarEntry[]; isToday: boolean, dayClass: DayClass }[] = [];
    for (let i = 0; i < 14; i++) {
      const day = new Date(start);
      day.setDate(start.getDate() + i);

      const entriesForDay = activeEntries
        .filter((entry) => this.isSameDay(day, entry.scheduledAt))
        .sort((a, b) => {
          const pinnedCompare = (a.pinned ? -1 : 0) - (b.pinned ? -1 : 0);
          if (pinnedCompare !== 0) return pinnedCompare;
          return this.asDate(a).getTime() - this.asDate(b).getTime();
        });

      slots.push({
        date: day,
        label: i === 0 ? 'Today' : day.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }),
        entries: entriesForDay,
        isToday: i === 0,
        dayClass: this.dayClass(day),
      });
    }

    this.daySlots = slots;

    this.pastEntries = activeEntries
      .filter((entry) => {
        const date = this.asDate(entry);
        if (isNaN(date.getTime())) {
          return false;
        }
        return this.dayKey(date) < this.dayKey(start);
      })
      .sort((a, b) => this.asDate(b).getTime() - this.asDate(a).getTime());

    this.beyondEntries = this.upcomingEntries.filter((entry) => {
      const date = this.asDate(entry);
      return date.getTime() > end.getTime();
    });
  }

  private isSameDay(day: Date, value: Date | string | undefined): boolean {
    if (!value) return false;
    const date = value instanceof Date ? value : new Date(value);
    return (
      date.getFullYear() === day.getFullYear() &&
      date.getMonth() === day.getMonth() &&
      date.getDate() === day.getDate()
    );
  }

  private startOfToday(): Date {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    return start;
  }

  isPast(entry: CalendarEntry): boolean {
    const date = this.asDate(entry);
    if (isNaN(date.getTime())) {
      return false;
    }
    return this.dayKey(date) < this.dayKey(this.startOfToday());
  }

  private dayKey(date: Date): number {
    return date.getFullYear() * 10000 + (date.getMonth() + 1) * 100 + date.getDate();
  }

  private dayClass(date: Date): DayClass {
    const classes: DayClass[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return classes[date.getDay()];
  }

  weeksUntil(entry: CalendarEntry): string {
    const target = this.asDate(entry);
    const today = this.startOfToday();
    const diffMs = target.getTime() - today.getTime();
    const weeks = Math.max(0, Math.ceil(diffMs / (7 * 24 * 60 * 60 * 1000)));
    return weeks === 1 ? '1 week' : `${weeks} weeks`;
  }
}

type DayClass = 'sunday' | 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday';
